import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import FarmersNav from "@/components/FarmersNav";
import { renderBlock } from "@/components/farmer_site/BlockRenderer";
import { FarmerSiteHeader, FarmerSiteFooter } from "@/components/farmer_site/FarmerSiteChrome";
import { themeVars } from "@/lib/farmer_site_themes";
import {
  ArrowLeft, ArrowRight, Sparkle, Storefront, Globe, MagicWand, Check,
  Plant, LinkSimple, CheckCircle, Warning, PencilSimple, Palette, Rocket,
} from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";

/**
 * FarmersWebsiteImport — /farmers/websites/import
 *
 * Paste your existing farm site URL, we crawl it + extract structured
 * content with AI, then show a fully-rendered preview you can tweak
 * and launch as your new Farmers Pulse site.
 *
 * State machine:
 *   idle -> crawling -> preview -> applying -> done
 *   idle -> error
 */
export default function FarmersWebsiteImport() {
  const nav = useNavigate();
  const { user, requireSignIn } = useAuth();
  const [url, setUrl] = useState("");
  const [stage, setStage] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(null);
  const [starter, setStarter] = useState(null);

  const startPreview = async () => {
    if (!user) return requireSignIn();
    if (!url.trim()) return toast.error("Paste your farm site URL first.");
    setError(null);
    setStage("crawling");
    try {
      const { data } = await http.post("/farmers/websites/import/preview", { url: url.trim() });
      setPreview(data);
      setDraftId(data.draft_id);
      setOverrides({});
      setTheme(data.draft?.suggested_theme || "greenfield");
      setStarter(data.draft?.suggested_starter || "farm-seller");
      setStage("preview");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't reach that website. Double-check the URL and try again.");
      setStage("error");
    }
  };

  const applyDraft = async () => {
    if (!draftId) return;
    setStage("applying");
    try {
      const drOver = { ...overrides };
      if (theme && theme !== preview.draft.suggested_theme) drOver.suggested_theme = theme;
      if (starter && starter !== preview.draft.suggested_starter) drOver.suggested_starter = starter;
      const { data } = await http.post("/farmers/websites/import/apply", {
        draft_id: draftId,
        draft_overrides: drOver,
      });
      toast.success(`Your site "${data.website.farm_name}" is ready!`);
      nav(fpPath(`/websites/${data.website.id}`));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't apply the draft — try again.";
      if (e?.response?.status === 409) {
        toast.error("You already have an active farm site. Archive it in the editor first.");
        nav(fpPath("/websites"));
      } else {
        toast.error(detail);
      }
      setStage("preview");
    }
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <MagicWand size={40} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <h1 className="mt-4 font-serif-display text-4xl">
            Create a custom website for you or Clone/Recreate your existing Website from any URL/Website Link
          </h1>
          <p className="mt-2 text-[#3A3830]">Sign in to try it — we'll rebuild your site block-by-block.</p>
          <button onClick={requireSignIn}
            className="mt-6 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 text-sm"
            data-testid="import-signin-cta">Sign in to continue</button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 pb-24 px-5 md:px-10" data-testid="farmers-website-import">
      <FarmersNav />
      <div className="max-w-5xl mx-auto">
        <Link to={fpPath("/websites")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farm sites
        </Link>

        <header className="mt-3 flex items-start gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A] flex items-center gap-1">
              <MagicWand size={12} weight="fill" /> Clone from URL &middot; AI import
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              Create a custom website for you or Clone/Recreate your existing Website from any URL/Website Link.
            </h1>
            <p className="mt-3 text-[#3A3830]">
              Paste the address of your current site or any farm website you like. We'll crawl the pages, read the content, and rebuild it as a modular Farmers Pulse site — themes, products, story, contact — you can edit block-by-block. Prefer to start fresh? <Link to={fpPath("/websites/new")} className="underline hover:text-[#151312]">Create a custom website from scratch</Link>.
            </p>
          </div>
        </header>

        {stage === "idle" || stage === "error" || stage === "crawling" ? (
          <section className="mt-8 rounded-3xl border border-black/10 bg-white p-6 md:p-10">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center gap-1">
                <LinkSimple size={11} weight="fill" /> Your current farm website
              </div>
              <div className="mt-2 flex flex-col md:flex-row items-stretch gap-2">
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.firpointfarms.com/"
                  disabled={stage === "crawling"}
                  className="flex-1 rounded-full border border-black/15 bg-white px-4 py-3 text-sm outline-none disabled:opacity-60 focus:border-[#B85F2A]"
                  data-testid="import-url-input"
                  onKeyDown={(e) => e.key === "Enter" && startPreview()} />
                <button onClick={startPreview} disabled={stage === "crawling"}
                  className="rounded-full bg-[#B85F2A] text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-1.5 justify-center disabled:opacity-60"
                  data-testid="import-start-cta">
                  {stage === "crawling" ? (
                    <>Working<Dots /></>
                  ) : (
                    <><Sparkle size={12} weight="fill" /> Clone this site</>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-[#3A3830]">
                Works with any public farm site. Nothing is copied to your public site until you press <em>Launch</em>.
              </p>
            </label>

            {stage === "crawling" && (
              <div className="mt-6 rounded-2xl bg-[#F5EBDC] p-5 flex items-center gap-3" data-testid="import-crawling">
                <Sparkle size={16} weight="fill" className="text-[#B85F2A] animate-pulse" />
                <div className="text-sm text-[#3A3830]">
                  Reading your site &amp; extracting content… this usually takes 15-30 seconds.
                </div>
              </div>
            )}

            {stage === "error" && (
              <div className="mt-6 rounded-2xl bg-red-50 border border-red-200 p-5 flex items-start gap-3" data-testid="import-error">
                <Warning size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <div className="font-medium">Couldn't clone that site.</div>
                  <div className="mt-0.5">{error}</div>
                </div>
              </div>
            )}

            <div className="mt-8 grid md:grid-cols-3 gap-3">
              <Bullet icon={Globe} title="Crawls up to 7 pages" body="Homepage, about, products, contact, story — we grab the useful ones." />
              <Bullet icon={MagicWand} title="Structured AI extract" body="Reads your content and turns it into farm name, story, contact, products." />
              <Bullet icon={PencilSimple} title="Fully editable after" body="Preview first, then tweak every block, page, theme and product." />
            </div>
          </section>
        ) : null}

        {stage === "preview" && preview && (
          <PreviewStage
            preview={preview}
            overrides={overrides}
            setOverrides={setOverrides}
            theme={theme}
            setTheme={setTheme}
            starter={starter}
            setStarter={setStarter}
            onApply={applyDraft}
            onRestart={() => { setStage("idle"); setPreview(null); setDraftId(null); }}
          />
        )}

        {stage === "applying" && (
          <section className="mt-8 rounded-3xl border border-black/10 bg-white p-10 text-center" data-testid="import-applying">
            <Sparkle size={40} weight="fill" className="mx-auto text-[#B85F2A] animate-pulse" />
            <h2 className="mt-4 font-serif-display text-3xl">Building your Farmers Pulse site…</h2>
            <p className="mt-2 text-sm text-[#3A3830]">Seeding pages, products &amp; content. You'll be dropped into the editor in a moment.</p>
          </section>
        )}
      </div>
    </main>
  );
}

function Dots() {
  return (
    <span className="inline-flex ml-1 gap-0.5">
      <span className="animate-pulse">.</span>
      <span className="animate-pulse [animation-delay:100ms]">.</span>
      <span className="animate-pulse [animation-delay:200ms]">.</span>
    </span>
  );
}

function Bullet({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <Icon size={18} weight="fill" className="text-[#B85F2A]" />
      <div className="mt-2 text-sm font-medium">{title}</div>
      <div className="mt-0.5 text-xs text-[#3A3830]">{body}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Preview stage — edit extracted content + preview rendered site     */
/* ------------------------------------------------------------------ */
function PreviewStage({ preview, overrides, setOverrides, theme, setTheme, starter, setStarter, onApply, onRestart }) {
  const d = { ...preview.draft, ...overrides };
  const set = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));
  const setLoc = (k, v) => setOverrides((o) => ({ ...o, location: { ...(o.location || preview.draft.location || {}), [k]: v } }));
  const setContact = (k, v) => setOverrides((o) => ({ ...o, contact: { ...(o.contact || preview.draft.contact || {}), [k]: v } }));

  // Build a mock site + a rendered "home" block for preview
  const mockSite = {
    id: "preview",
    slug: "preview",
    farm_name: d.farm_name || "Your farm",
    tagline: d.tagline,
    short_description: d.short_description,
    farm_types: d.farm_types || [],
    hero_image_url: d.hero_image_url,
    location: { city: d.location?.city, state: d.location?.state, country: d.location?.country },
    contact: d.contact || {},
    navigation: [
      { slug: "home", title: "Home" },
      { slug: "products", title: "Products" },
      { slug: "about", title: "About" },
      { slug: "contact", title: "Contact" },
    ],
    social_links: d.social_links || {},
    theme_id: theme,
    owner_uid: "preview",
  };
  const mockContent = {
    products: (d.products || []).map((p, i) => ({
      id: `p_${i}`, name: p.name, category: p.category || "other",
      unit: p.unit || "each", price: p.price, currency: p.currency,
      photos: p.photo_url ? [p.photo_url] : [],
      description: p.description, fresh_today: false, is_organic: false,
    })),
    deals: [], surplus: [], journal: [], media: [], fresh_today: [],
  };
  const themeStyle = themeVars(theme);
  const themeOptions = ["farm-market", "fresh-harvest", "pine-hollow", "greenfield", "ocean-fresh", "greenhorizon", "meadow-lane", "green-harvest"];
  const starterOptions = ["basic-farm", "farm-seller", "farm-stand", "market-vendor", "simple-farm", "full-farm"];

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-4" data-testid="import-preview">
      {/* LEFT — editable extract */}
      <aside className="space-y-4">
        <div className="rounded-2xl border border-black/10 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center justify-between">
            <span><CheckCircle size={11} weight="fill" className="inline mr-1 text-emerald-600" /> Crawl complete</span>
            <span>{preview.crawl_summary?.pages_crawled} pages</span>
          </div>
          <div className="mt-2 text-xs text-[#3A3830]">
            {preview.source_url}
          </div>
          <div className="mt-1 text-[10px] font-mono text-[#3A3830]">
            method: {d.extraction_method || "auto"}
          </div>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Farm identity</div>
          <Row label="Farm name">
            <input value={d.farm_name || ""} onChange={(e) => set("farm_name", e.target.value)}
              className="input" data-testid="preview-farm-name" />
          </Row>
          <Row label="Tagline">
            <input value={d.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
              className="input" data-testid="preview-tagline" />
          </Row>
          <Row label="Short description">
            <textarea value={d.short_description || ""} rows={3}
              onChange={(e) => set("short_description", e.target.value)}
              className="input" data-testid="preview-short-desc" />
          </Row>
          <Row label="Story / about">
            <textarea value={d.story || ""} rows={5}
              onChange={(e) => set("story", e.target.value)}
              className="input" data-testid="preview-story" />
          </Row>
          <Row label="Farm types" hint="comma-separated">
            <input value={(d.farm_types || []).join(", ")}
              onChange={(e) => set("farm_types", e.target.value.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6))}
              className="input" data-testid="preview-farm-types" />
          </Row>
          <Row label="Hero image">
            <input value={d.hero_image_url || ""} onChange={(e) => set("hero_image_url", e.target.value)}
              placeholder="https://…" className="input font-mono text-[11px]" data-testid="preview-hero-url" />
          </Row>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Location</div>
          <Row label="City"><input value={d.location?.city || ""} onChange={(e) => setLoc("city", e.target.value)} className="input" data-testid="preview-city" /></Row>
          <Row label="State"><input value={d.location?.state || ""} onChange={(e) => setLoc("state", e.target.value)} className="input" data-testid="preview-state" /></Row>
          <Row label="Country"><input value={d.location?.country || ""} onChange={(e) => setLoc("country", e.target.value)} className="input" data-testid="preview-country" /></Row>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Contact</div>
          <Row label="Email"><input value={d.contact?.email || ""} onChange={(e) => setContact("email", e.target.value)} className="input" data-testid="preview-email" /></Row>
          <Row label="Phone"><input value={d.contact?.phone || ""} onChange={(e) => setContact("phone", e.target.value)} className="input" data-testid="preview-phone" /></Row>
          <Row label="WhatsApp"><input value={d.contact?.whatsapp || ""} onChange={(e) => setContact("whatsapp", e.target.value)} className="input" data-testid="preview-whatsapp" /></Row>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center gap-1">
            <Palette size={11} weight="fill" /> Look &amp; pages
          </div>
          <Row label="Theme" hint="AI suggested — you can change">
            <select value={theme} onChange={(e) => setTheme(e.target.value)} className="input" data-testid="preview-theme">
              {themeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Row>
          <Row label="Starter pack">
            <select value={starter} onChange={(e) => setStarter(e.target.value)} className="input" data-testid="preview-starter">
              {starterOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Row>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center justify-between">
            <span>Products extracted</span>
            <span>{(d.products || []).length}</span>
          </div>
          {(d.products || []).length === 0 ? (
            <div className="text-xs text-[#3A3830]">None found — you can add them in the editor.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {(d.products || []).slice(0, 12).map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check size={11} weight="bold" className="text-emerald-600 shrink-0" />
                  <span className="truncate">{p.name}</span>
                  <span className="ml-auto text-xs text-[#3A3830] capitalize">{p.category}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl bg-[#151312] text-[#F5EBDC] p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono opacity-70">
            <Rocket size={11} weight="fill" className="inline mr-1" /> Ready to launch
          </div>
          <p className="text-xs opacity-80">
            We'll seed pages, blocks and products with the content above. You can edit everything in the site editor after.
          </p>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={onRestart}
              className="flex-1 rounded-full border border-white/20 px-3 py-2 text-xs hover:bg-white/10"
              data-testid="preview-restart">
              Start over
            </button>
            <button onClick={onApply}
              className="flex-1 rounded-full bg-[#B85F2A] text-white px-3 py-2 text-xs font-medium inline-flex items-center gap-1 justify-center"
              data-testid="preview-apply">
              Launch site <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* RIGHT — live preview */}
      <section className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="import-preview-render">
        <div className="border-b border-black/10 px-4 py-2 flex items-center justify-between bg-neutral-50">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">
            Live preview · theme <span className="text-[#B85F2A]">{theme}</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">
            <Sparkle size={10} weight="fill" className="inline mr-0.5 text-[#B85F2A]" /> updates as you edit
          </div>
        </div>
        <div style={{ ...themeStyle, background: "var(--fs-bg)" }}>
          <FarmerSiteHeader site={mockSite} />
          {/* Render the common Phase-1 blocks with the extracted content. */}
          {renderBlock({ block: { id: "b1", kind: "hero", config: {} }, site: mockSite, content: mockContent })}
          {renderBlock({ block: { id: "b2", kind: "trust-badges", config: {} }, site: mockSite, content: mockContent })}
          {mockContent.products.length > 0 &&
            renderBlock({ block: { id: "b3", kind: "products", config: {} }, site: mockSite, content: mockContent })}
          {d.story && renderBlock({
            block: { id: "b4", kind: "farm-story", config: { body: d.story, title: "Our story" } },
            site: mockSite, content: mockContent,
          })}
          {(d.contact?.email || d.contact?.phone) &&
            renderBlock({ block: { id: "b5", kind: "farm-info", config: {} }, site: mockSite, content: mockContent })}
          {renderBlock({ block: { id: "b6", kind: "contact", config: {} }, site: mockSite, content: mockContent })}
          <FarmerSiteFooter site={mockSite} />
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid rgba(0,0,0,0.15);
          padding: 6px 10px;
          font-size: 13px;
          outline: none;
          background: white;
        }
        .input:focus { border-color: #B85F2A; }
      `}</style>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">{label}</div>
      {hint && <div className="text-[11px] text-[#3A3830]">{hint}</div>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
